import { useModals } from "@/features/modals/hooks";
import {
  type OpportunitiesSearchFilters,
  type OpportunitiesSearchOptions,
  type OpportunitySearch,
  type Team,
  type UserInfo,
  getUserInfo,
  useMultiupdateOpportunity,
  useProfile,
} from "@repo/api";
import { Text } from "@repo/skapa";
import { t } from "@repo/translation";
import { CoworkerAndTeamSelectorListItem, useToast } from "@repo/ui";
import { getErrorMessage, isDefined } from "@repo/utils";
import { useEffect, useState } from "react";

import { type EditModalMode } from "../utils";
import { MultiUpdateModal, createSaveSucess } from "./MultiUpdateModal";

interface AssignProjectsModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<EditModalMode>>;
  selectedOpportunities: OpportunitySearch[];
  setSelectedOpportunities: React.Dispatch<
    React.SetStateAction<OpportunitySearch[]>
  >;
  options: OpportunitiesSearchOptions;
  filters: OpportunitiesSearchFilters;
}
export const AssignProjectsModal = ({
  isOpen,
  setIsOpen,
  selectedOpportunities,
  setSelectedOpportunities,
  options,
  filters,
}: AssignProjectsModalProps) => {
  const { updateToast } = useToast();
  const { data: profile } = useProfile();
  const { pushModal } = useModals();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedCoworker, setSelectedCoworker] = useState<
    UserInfo | undefined
  >(getUserInfo(profile));
  const [selectedTeam, setSelectedTeam] = useState<Team | undefined>(undefined);

  useEffect(() => {
    setSelectedCoworker(getUserInfo(profile));
  }, [profile]);

  const updateProjects = useMultiupdateOpportunity(filters, options);

  const onSave = (selectedProjects: OpportunitySearch[]) => {
    setIsLoading(true);
    updateProjects(
      {
        coworkerId: selectedCoworker?.emailId,
        teamId: String(selectedTeam?.teamId),
      },
      selectedProjects.map((el) => el.opportunityId)
    )
      .then(
        createSaveSucess({
          setSelectedOpportunities,
          selectedOpportunities,
          selectedProjects,
          updateToast,
          setIsOpen,
          title: t("prompt.projects_assigned_result", {
            numberOfProjects: selectedProjects.length,
            givenName: selectedCoworker?.givenName ?? selectedTeam?.teamName,
            surname: selectedCoworker?.surname,
          }),
        })
      )
      .catch((err) => {
        updateToast({
          id: String(selectedProjects.map((el) => el.opportunityId)),
          title: t("prompt.assign_projects_failed"),
          message: getErrorMessage(err),
          type: "error",
        });
      })
      .finally(() => {
        setSelectedCoworker(getUserInfo(profile));
        setIsLoading(false);
      });
  };

  return (
    <MultiUpdateModal
      title={t("action.assign")}
      isOpen={isOpen}
      isLoading={isLoading}
      setIsOpen={setIsOpen}
      selectedOpportunities={selectedOpportunities}
      onSave={onSave}
    >
      <Text textStyle="Heading.S">{t("action.assign_to")}</Text>

      <CoworkerAndTeamSelectorListItem
        selectionMode={{
          coworkerSelectionMode: "SINGLE",
          selectOnlyTeam: true,
        }}
        title={t("label.assigned_to")}
        pushModal={pushModal}
        selected={
          selectedTeam
            ? {
                kind: "team",
                team: selectedTeam,
                assignedToTeam: true,
                members: (selectedTeam.teamMembers ?? []).map((m) => ({
                  ...m,
                  selected: true,
                })),
              }
            : [selectedCoworker?.emailId].filter(isDefined)
        }
        searchContext="PROJECT"
        setSelected={(s) => {
          if (s && !Array.isArray(s)) {
            setSelectedCoworker(undefined);
            setSelectedTeam(s.team);
          } else {
            setSelectedCoworker(s?.[0]);
            setSelectedTeam(undefined);
          }
        }}
      />
    </MultiUpdateModal>
  );
};
